import {
  AcademicCapIcon,
  CalendarDaysIcon,
  MapPinIcon,
  ClockIcon,
  GlobeAltIcon,
  PlayIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { PublicClassroom } from '@/types/course';
import { formatPriceSimple } from '@/utils/courseFormat';
import { formatDbDateVi } from '@/utils/dbDateDisplay';

interface CoursePurchaseCardProps {
  priceNode: ReactNode;
  methodLabel: string;
  timeInHour?: number;
  timeInLesson?: number;
  upcomingClasses?: PublicClassroom[];
  /** Khóa video tự học: hiện học mọi chỗ mọi nơi */
  isSelfPaced?: boolean;
  onRegister: () => void;
  ctaLabel?: string;
  isEnrolled?: boolean;
  className?: string;
}

const CoursePurchaseCard = ({
  priceNode,
  methodLabel,
  timeInHour,
  timeInLesson,
  upcomingClasses = [],
  isSelfPaced = false,
  onRegister,
  ctaLabel = 'Đăng ký khóa học',
  isEnrolled = false,
  className = '',
}: CoursePurchaseCardProps) => {
  const CtaIcon = isEnrolled ? PlayIcon : AcademicCapIcon;

  return (
    <aside
      id="hoc-phi"
      className={`rounded-eh border border-eh-border bg-m2-surface-lowest p-5 shadow-eh-soft ${className}`}
    >
      <div className="mb-4">{priceNode}</div>

      <button
        type="button"
        onClick={onRegister}
        className="eh-btn w-full text-base"
      >
        <CtaIcon className="h-5 w-5" />
        {ctaLabel}
      </button>

      <ul className="mt-5 space-y-2 text-sm text-m2-on-surface-variant border-t border-m2-outline-variant pt-4">
        <li className="flex items-center gap-2">
          <span className="font-medium text-m2-on-surface">Hình thức:</span>
          {methodLabel}
        </li>
        {typeof timeInHour === 'number' && timeInHour > 0 && (
          <li className="flex items-center gap-2">
            <ClockIcon className="h-4 w-4 shrink-0" />
            {timeInHour} giờ học
          </li>
        )}
        {typeof timeInLesson === 'number' && timeInLesson > 0 && !isSelfPaced && (
          <li className="flex items-center gap-2">
            <span className="font-medium text-m2-on-surface">Số buổi:</span>
            {timeInLesson}
          </li>
        )}
      </ul>

      {isSelfPaced ? (
        <div className="mt-5 border-t border-m2-outline-variant pt-4">
          <div className="flex items-start gap-2 rounded-eh bg-eh-mint p-3 text-sm text-m2-on-surface">
            <GlobeAltIcon className="h-5 w-5 shrink-0 text-m2-primary mt-0.5" />
            <div>
              <p className="font-semibold text-m2-on-surface">Học mọi chỗ, mọi nơi</p>
              <p className="mt-0.5 text-m2-on-surface-variant">
                Video tự học — đăng ký và học bất cứ lúc nào, không phụ thuộc lịch khai giảng.
              </p>
            </div>
          </div>
        </div>
      ) : (
        upcomingClasses.length > 0 && (
          <div className="mt-5 border-t border-m2-outline-variant pt-4">
            <p className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-m2-on-surface-variant">
              <CalendarDaysIcon className="h-4 w-4" />
              Lịch khai giảng
            </p>
            <ul className="space-y-3 max-h-72 overflow-y-auto">
              {upcomingClasses.map((classroom) => (
                <li
                  key={classroom.id}
                  className="rounded-eh border border-eh-border bg-eh-cream p-3 text-sm"
                >
                  <div className="font-semibold text-m2-on-surface">
                    {classroom.name || classroom.code}
                  </div>
                  <div className="mt-2 text-m2-on-surface-variant space-y-1.5">
                    {classroom.startDate && (
                      <div className="flex items-center gap-1.5">
                        <CalendarDaysIcon className="h-3.5 w-3.5 shrink-0 text-m2-primary" />
                        <span>
                          <span className="font-medium text-m2-on-surface">Khai giảng:</span>{' '}
                          {formatDbDateVi(classroom.startDate)}
                          {classroom.endDate &&
                            ` — ${formatDbDateVi(classroom.endDate)}`}
                        </span>
                      </div>
                    )}
                    {(classroom.studyTime || classroom.schedule) && (
                      <div className="flex items-start gap-1.5">
                        <BookOpenIcon className="h-3.5 w-3.5 shrink-0 text-m2-primary mt-0.5" />
                        <span>
                          <span className="font-medium text-m2-on-surface">Lịch học:</span>{' '}
                          {classroom.studyTime || classroom.schedule}
                        </span>
                      </div>
                    )}
                    {classroom.address && (
                      <div className="flex items-center gap-1.5">
                        <MapPinIcon className="h-3.5 w-3.5 shrink-0 text-m2-primary" />
                        <span>
                          <span className="font-medium text-m2-on-surface">Địa điểm:</span>{' '}
                          {classroom.address}
                        </span>
                      </div>
                    )}
                    {typeof classroom.fee === 'number' && classroom.fee > 0 && (
                      <div className="mt-1 text-m2-primary font-semibold">
                        {formatPriceSimple(classroom.fee)}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )
      )}
    </aside>
  );
};

export default CoursePurchaseCard;
